import { z } from 'zod';

const TABLE_PATH_PATTERN = /^\/workspace\/tables\/[a-f0-9]{24}\.jsonl$/;

export const renderArtifactReceiptSchema = z.object({
    component: z.enum(['table', 'shifts', 'caregivers', 'client_detail']),
    context_entity: z
        .enum(['shift', 'caregiver', 'client'])
        .nullable()
        .optional(),
    title: z.string().max(80).optional(),
    path: z
        .string()
        .regex(/^\/workspace\/(?:tables|presentations)\/[a-f0-9]{24}\.jsonl$/),
    format: z.enum(['csv', 'jsonl']),
    row_count: z.number().int().nonnegative(),
    byte_size: z.number().int().nonnegative(),
    columns: z.array(z.string().max(128)).max(64),
    columns_truncated: z.boolean(),
    content_sha256: z.string().regex(/^[a-f0-9]{64}$/),
    suggested_component: z
        .enum(['shifts', 'caregivers', 'client_detail'])
        .optional(),
    event_id: z.uuid().optional(),
    page_offsets: z.array(z.number().int().nonnegative()).max(501).optional(),
    selection_actions: z
        .array(
            z.object({
                label: z.string().trim().min(1).max(48),
                prompt: z.string().trim().min(1).max(500),
            }),
        )
        .max(4)
        .optional(),
});

export const renderArtifactOutputSchema = z.object({
    render_artifact: renderArtifactReceiptSchema,
});

// Receipts whose rows the browser can list: a generic table, or a domain
// result (shifts, caregivers, one client). Only the outreach detail card
// has no rows behind it.
const renderedRowsReceiptSchema = renderArtifactReceiptSchema.extend({
    component: z.enum(['table', 'shifts', 'caregivers', 'client_detail']),
    title: z.string().trim().min(1).max(80).optional(),
    row_count: z.number().int().positive(),
    columns: z.array(z.string().max(128)).min(1).max(64),
});
const renderedRowsOutputSchema = z.object({
    render_artifact: renderedRowsReceiptSchema,
});
const agencyRulesRenderArtifactReceiptSchema = renderArtifactReceiptSchema
    .omit({ component: true })
    .extend({
        component: z.literal('agency_rules'),
        format: z.literal('jsonl'),
        row_count: z.number().int().positive(),
        columns: z.tuple([z.literal('rule')]),
        columns_truncated: z.literal(false),
    });
const agencyRulesRenderArtifactOutputSchema = z.object({
    render_artifact: agencyRulesRenderArtifactReceiptSchema,
});

export type RenderedRowsReceipt = z.infer<typeof renderedRowsReceiptSchema>;
export type GenericTableReceipt = RenderedRowsReceipt &
    Readonly<{ readonly component: 'table' }>;

// V3 chat lists every rows receipt as a table until domain cards exist.
export function parseRenderedRowsReceipt(
    toolName: string,
    output: unknown,
): {
    readonly receipt: RenderedRowsReceipt;
    readonly workspacePath: string;
} | null {
    if (toolName !== 'render_dynamic_ui') {
        return null;
    }
    const parsed = renderedRowsOutputSchema.safeParse(output);
    if (!parsed.success) {
        return null;
    }
    const receipt = parsed.data.render_artifact;
    return {
        receipt,
        workspacePath: receipt.path.slice('/workspace/'.length),
    };
}

// Legacy chat picks a widget by asking each adapter whether it accepts the
// receipt, so its table adapter must keep refusing domain results.
export function parseGenericTableReceipt(
    toolName: string,
    output: unknown,
): {
    readonly receipt: GenericTableReceipt;
    readonly workspacePath: string;
} | null {
    const rows = parseRenderedRowsReceipt(toolName, output);
    if (
        rows === null ||
        rows.receipt.component !== 'table' ||
        !TABLE_PATH_PATTERN.test(rows.receipt.path)
    ) {
        return null;
    }
    return {
        receipt: { ...rows.receipt, component: 'table' },
        workspacePath: rows.workspacePath,
    };
}

export function parseAgencyRulesReceipt(
    toolName: string,
    output: unknown,
): {
    readonly receipt: z.infer<typeof agencyRulesRenderArtifactReceiptSchema>;
    readonly workspacePath: string;
} | null {
    if (toolName !== 'render_dynamic_ui') {
        return null;
    }
    const parsed = agencyRulesRenderArtifactOutputSchema.safeParse(output);
    if (!parsed.success) {
        return null;
    }
    const receipt = parsed.data.render_artifact;
    const workspacePath = receipt.path.slice('/workspace/'.length);
    if (!workspacePath.startsWith('presentations/')) {
        return null;
    }
    return { receipt, workspacePath };
}
